"use client"

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react"
import type { Client } from "./marketing-api"
import { getClients } from "./marketing-api"
import { useAuth } from "./auth-context"
import { resolvePortalRole, RolePermissions } from "./rbac"

interface ClientContextType {
  activeClient: Client | null
  setActiveClient: (client: Client | null) => void
  clearClient: () => void
  assignedClientIds: string[]
  assignedClients: Client[]
  canSwitchClients: boolean
}

const STORAGE_KEY = "active_client"

const ClientContext = createContext<ClientContextType>({
  activeClient: null,
  setActiveClient: () => {},
  clearClient: () => {},
  assignedClientIds: [],
  assignedClients: [],
  canSwitchClients: true,
})

export function ClientProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  const normalizeClient = (c: any): Client | null => {
    if (!c) return null
    // MongoDB stores _id as { $oid: "..." } when serialized naively — flatten it
    const id = c._id?.$oid || c._id?.toString?.() || String(c._id || "")
    return { ...c, _id: id }
  }

  const [activeClient, setActiveClientState] = useState<Client | null>(() => {
    if (typeof window === "undefined") return null
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return normalizeClient(stored ? JSON.parse(stored) : null)
    } catch {
      return null
    }
  })

  const normalizedRole = resolvePortalRole(user)
  const assignedClientIds = Array.isArray(user?.assignedClientIds)
    ? user.assignedClientIds
        .map((id) => (typeof id === "string" ? id : (id as any)?._id || String(id || "")))
        .filter(Boolean)
    : []
  const canSwitchClients = RolePermissions.canSwitchClients(normalizedRole, assignedClientIds.length)
  const [assignedClients, setAssignedClients] = useState<Client[]>([])

  const setActiveClient = useCallback((client: Client | null) => {
    const normalized = normalizeClient(client)
    if (!normalized) {
      setActiveClientState(null)
      localStorage.removeItem(STORAGE_KEY)
      return
    }

    if (!canSwitchClients && assignedClientIds.length > 0 && !assignedClientIds.includes(normalized._id)) {
      return
    }

    setActiveClientState(normalized)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized))
  }, [assignedClientIds, canSwitchClients])

  const clearClient = useCallback(() => {
    if (!canSwitchClients) return
    setActiveClientState(null)
    localStorage.removeItem(STORAGE_KEY)
  }, [canSwitchClients])

  useEffect(() => {
    if (assignedClientIds.length === 0) return
    if (activeClient && assignedClientIds.includes(activeClient._id)) return

    // Only auto-select if user has exactly 1 assigned client
    // If they have 2+, they should manually choose which one to work with
    if (assignedClientIds.length === 1) {
      const fallback = assignedClients[0]
      if (!fallback) return

      setActiveClientState(fallback)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fallback))
    }
  }, [activeClient, assignedClientIds, assignedClients])

  return (
    <ClientContext.Provider value={{ activeClient, setActiveClient, clearClient, assignedClientIds, assignedClients, canSwitchClients }}>
      {children}
    </ClientContext.Provider>
  )
}

export function useClientContext() {
  return useContext(ClientContext)
}
